import { applyVariantOperation } from "./variant-review";
import { reverseOwnedChange } from "./record-fields";
import {
  readPreconditions,
  changedSummary,
  preconditionConflicts,
} from "./agent-preconditions";
import { applyLayoutOperation } from "./agent-layout";
import { applyImageOperation } from "./agent-images";
import { normalizeAgentRequest } from "./agent-parent";
import {
  preserveTextStyle,
  editTextRange,
  richTextPlain,
} from "./agent-text-edit";
import { richTextValidator } from "@tldraw/tlschema";
import { applyTableOperation } from "./agent-tables";
import { applyPageOperation } from "./agent-pages";
import { captionHeight } from "./image-caption";
import type { RecordStorage, Transaction } from "./sync/storage";
import {
  createComment,
  createCommentThread,
  createCommentId,
  createCommentThreadId,
} from "@tldraw/tlschema";
import { getIndexAbove } from "@tldraw/utils";
import type {
  TLRecord,
  TLShape,
  TLRichText,
  TLShapeId,
  TLAssetId,
} from "@tldraw/tlschema";
import { nativeDocument } from "./document-session";
import { applyWorkspaceOperation } from "./workspace-agent-actions";
import { reviewDraft } from "./updates";
import { schema, type Sheet } from "./schema";
import { checkedSceneSpec } from "./scene-spec";
import { canEdit, type SessionMeta } from "./authorization";
import {
  agentBatch,
  agentUndo,
  AgentError,
  AgentConflict,
  type AgentBatch,
  type AgentUndo,
} from "./agent-contract";

type Change = { before: TLRecord | null; after: TLRecord | null };
export type AgentReceipt = {
  batchId: string;
  actorId: string;
  actorName: string;
  createdAt: number;
  revision: number;
  request: string;
  changes: Record<string, Change>;
};
/** Implementations must use the SAME SQLite transaction as the board storage. */
export interface AgentJournal {
  get(id: string): AgentReceipt | undefined;
  put(receipt: AgentReceipt): void;
}
export type AgentSnapshot = ReturnType<typeof readAgentBoard>;
export function readAgentBoard(
  storage: RecordStorage<TLRecord>,
  boardId: string,
  since?: number,
) {
  return storage.transaction((txn) => {
    const records = [...txn.values()];
    return {
      boardId,
      ...(since !== undefined
        ? { changes: changedSummary(storage, since) }
        : {}),
      revision: txn.getClock(),
      schema: schema.serialize(),
      store: Object.fromEntries(records.map((r) => [r.id, r])),
      sheets: records
        .filter((r): r is Sheet => r.typeName === "shape" && r.type === "sheet")
        .sort(
          (a, b) =>
            Number(a.meta.order ?? 1e9) - Number(b.meta.order ?? 1e9) ||
            (a.index < b.index ? -1 : a.index > b.index ? 1 : 0) ||
            a.id.localeCompare(b.id),
        )
        .map((s) => ({
          id: s.id,
          name: s.props.name,
          x: s.x,
          y: s.y,
          w: s.props.w,
          h: s.props.h,
          pageId: s.parentId,
        })),
    };
  }).result;
}
export function resolveSheet(snapshot: AgentSnapshot, reference: string) {
  const matches = snapshot.sheets.filter(
    (s) =>
      s.id === reference || s.name.toLowerCase() === reference.toLowerCase(),
  );
  if (matches.length !== 1)
    throw new AgentError(
      400,
      "Sheet name is missing or ambiguous. Choose a stable sheet ID from list_sheets.",
    );
  return matches[0];
}
export const plainText = richTextPlain;
export function readAgentSheet(snapshot: AgentSnapshot, reference: string) {
  const sheet = resolveSheet(snapshot, reference);
  const shapes = Object.values(snapshot.store).filter(
    (r): r is TLShape => r.typeName === "shape",
  );
  const ids = new Set<string>([sheet.id]);
  for (let depth = 0; depth < 32; depth++) {
    const count = ids.size;
    for (const shape of shapes) if (ids.has(shape.parentId)) ids.add(shape.id);
    if (count === ids.size) break;
  }
  const objects = shapes
    .filter((s) => ids.has(s.id))
    .map((s) => ({
      ...s,
      text: "richText" in s.props ? plainText(s.props.richText) : undefined,
      coordinates:
        "parent-local canvas units; sheet children are relative to the sheet's top-left",
    }));
  const threads = Object.values(snapshot.store).filter(
    (r) =>
      r.typeName === "comment-thread" &&
      r.anchor.type === "shape" &&
      ids.has(r.anchor.shapeId),
  );
  const threadIds = new Set(threads.map((t) => t.id));
  const comments = Object.values(snapshot.store).filter(
    (r) => r.typeName === "comment" && threadIds.has(r.threadId),
  );
  const assets = Object.values(snapshot.store).filter(
    (r) =>
      r.typeName === "asset" &&
      objects.some((o) => "assetId" in o.props && o.props.assetId === r.id),
  );
  // Page-anchored comments cannot be assigned reliably by a server without rendered shape bounds.
  const pageThreads = Object.values(snapshot.store).filter(
    (r) =>
      r.typeName === "comment-thread" &&
      r.pageId === sheet.pageId &&
      r.anchor.type !== "shape",
  );
  const pageThreadIds = new Set(pageThreads.map((t) => t.id));
  const pageComments = Object.values(snapshot.store).filter(
    (r) => r.typeName === "comment" && pageThreadIds.has(r.threadId),
  );
  return {
    boardId: snapshot.boardId,
    revision: snapshot.revision,
    sheet,
    objects,
    preconditions: readPreconditions(
      snapshot.store,
      [...objects, ...assets, ...threads, ...comments].map((r) => r.id),
    ),
    assets,
    threads,
    comments,
    pageThreads,
    pageComments,
  };
}
const richText = (
  text: string,
  bold = false,
  ranges: { start: number; end: number }[] = [],
): TLRichText => {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  if (
    sorted.some(
      (r, i) =>
        r.start >= r.end ||
        r.end > text.length ||
        (i > 0 && r.start < sorted[i - 1].end),
    )
  )
    throw new AgentError(
      400,
      "Bold ranges must be non-overlapping UTF-16 offsets within the text.",
    );
  let offset = 0;
  return {
    type: "doc",
    content: text.split("\n").map((line) => {
      const begin = offset,
        end = begin + line.length;
      offset = end + 1;
      const points = [
        ...new Set([
          begin,
          end,
          ...sorted.flatMap((r) => [
            Math.max(begin, Math.min(end, r.start)),
            Math.max(begin, Math.min(end, r.end)),
          ]),
        ]),
      ].sort((a, b) => a - b);
      return {
        type: "paragraph",
        content: points.slice(0, -1).map((start, i) => ({
          type: "text",
          text: text.slice(start, points[i + 1]),
          ...(bold ||
          sorted.some((r) => r.start <= start && r.end >= points[i + 1])
            ? { marks: [{ type: "bold" }] }
            : {}),
        })),
      };
    }),
  };
};
const equal = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);
const fail = (message: string): never => {
  throw new AgentError(400, message);
};
function editable(txn: Transaction<TLRecord>, id: string): TLShape {
  const shape = txn.get(id);
  if (!shape || shape.typeName !== "shape") return fail("Shape not found.");
  let current: TLRecord | undefined = shape;
  for (let depth = 0; current?.typeName === "shape" && depth < 32; depth++) {
    if (current.isLocked)
      throw new AgentError(409, "Shape or parent is locked.");
    current = txn.get(current.parentId);
  }
  return shape;
}
export function applyAgentBatch(
  storage: RecordStorage<TLRecord>,
  journal: AgentJournal,
  actor: SessionMeta,
  input: unknown,
  validate: (txn: Transaction<TLRecord>) => void,
  undo = false,
) {
  if (!canEdit(actor.role))
    throw new AgentError(403, "An editor or owner is required.");
  const parsed = undo
    ? agentUndo.safeParse(input)
    : agentBatch.safeParse(normalizeAgentRequest(input));
  if (!parsed.success)
    throw new AgentError(
      400,
      parsed.error.issues
        .map((i) => `${i.path.join(".")}: ${i.message}`)
        .join("; "),
    );
  const command = parsed.data;
  const request = JSON.stringify(command);
  return storage.transaction(
    (txn) => {
      const existing = journal.get(command.batchId);
      if (existing) {
        // Normalize older receipts with the current defaults before comparing retries.
        const prior = (undo ? agentUndo : agentBatch).safeParse(
          JSON.parse(existing.request),
        );
        if (
          existing.actorId !== actor.user.id ||
          !prior.success ||
          JSON.stringify(prior.data) !== request
        )
          throw new AgentError(
            409,
            "Batch ID already used for a different request or actor.",
          );
        return {
          batchId: existing.batchId,
          revision: existing.revision,
          changedIds: Object.keys(existing.changes),
          replayed: true,
        };
      }
      if (
        command.revision !== txn.getClock() ||
        ("preconditions" in command && command.preconditions)
      ) {
        const conditions =
          "preconditions" in command ? command.preconditions : undefined;
        const conflicts =
          !undo && conditions
            ? preconditionConflicts(
                txn,
                conditions,
                (command as AgentBatch).operations,
              )
            : undefined;
        if (
          command.revision > txn.getClock() ||
          undo ||
          !conditions ||
          conflicts?.length
        )
          throw new AgentConflict(
            "Board changed. Read the changed records before retrying.",
            {
              expectedRevision: command.revision,
              ...changedSummary(storage, command.revision),
              conflicts,
              requiresFullRead:
                !conditions ||
                changedSummary(storage, command.revision).requiresFullRead,
            },
          );
      }
      const changes: Record<string, Change> = {};
      const set = (record: TLRecord) => {
        // Unlike socket writes, server transactions do not automatically run schema validation.
        schema.types[record.typeName].validate(record);
        if (!changes[record.id])
          changes[record.id] = {
            before: txn.get(record.id) ?? null,
            after: record,
          };
        changes[record.id].after = record;
        txn.set(record.id, record);
      };
      if (undo) {
        const target = journal.get((command as AgentUndo).undoBatchId);
        if (!target || target.actorId !== actor.user.id)
          throw new AgentError(404, "Your batch was not found.");
        if (
          Object.values(target.changes).some(
            (c) =>
              (c.after?.typeName === "comment" ||
                c.after?.typeName === "comment-thread") &&
              (!c.before ||
                !equal({ ...c.before, pageId: c.after.pageId }, c.after)),
          )
        )
          throw new AgentError(
            409,
            "Use the discussion controls to remove comments; comment batches are not undoable.",
          );
        for (const [id, change] of Object.entries(target.changes)) {
          if (
            !change.before &&
            [...txn.values()].some(
              (r) =>
                r.typeName === "comment-thread" &&
                r.anchor.type === "shape" &&
                r.anchor.shapeId === id &&
                !r.isDeleted,
            )
          )
            throw new AgentError(
              409,
              "A new discussion depends on this object.",
            );
          const inverse = reverseOwnedChange(txn.get(id) ?? null, change.before, change.after);
          if (!inverse)
            throw new AgentError(
              409,
              "A changed object has newer edits; undo would overwrite them.",
            );
          if (change.after?.typeName === "shape") editable(txn, id);
          changes[id] = inverse;
        }
        for (const [id, change] of Object.entries(changes)) {
          if (change.after) set(change.after);
          else txn.delete(id);
        }
      } else {
        for (const op of (command as AgentBatch).operations) {
          const remove = (id: string) => {
            const before = txn.get(id);
            if (!before) return;
            if (!changes[id]) changes[id] = { before, after: null };
            else changes[id].after = null;
            txn.delete(id);
          };
          if (applyWorkspaceOperation(txn, set, remove, op, actor)) continue;
          if (
            applyPageOperation(op, {
              txn,
              set,
              editable: (id) => editable(txn, id),
              userId: actor.user.id,
              batchId: command.batchId,
            })
          )
            continue;
          if (
            applyTableOperation(op, {
              txn,
              set,
              editable: (id) => editable(txn, id),
              userId: actor.user.id,
              batchId: command.batchId,
            })
          )
            continue;
          if (op.type === "add_table" || op.type === "edit_table") continue;
          if (
            applyImageOperation(op, {
              txn,
              set,
              editable: (id) => editable(txn, id),
              userId: actor.user.id,
              batchId: command.batchId,
            })
          )
            continue;
          if (
            applyLayoutOperation(op, {
              txn,
              set,
              editable: (id) => editable(txn, id),
              userId: actor.user.id,
              batchId: command.batchId,
              revision: command.revision,
            })
          )
            continue;
          if (
            applyVariantOperation(op, {
              txn,
              set,
              editable: (id) => editable(txn, id),
              userId: actor.user.id,
              userName: actor.user.name,
              batchId: command.batchId,
            })
          )
            continue;
          if (op.type === "set_variant_review") continue;
          if (
            op.type === "arrange" ||
            op.type === "fit_sheet" ||
            op.type === "resize_sheet" ||
            op.type === "place_relative"
          )
            continue;
          if (
            op.type === "place_image" ||
            op.type === "replace_image" ||
            op.type === "set_image_source"
          )
            continue;
          if (
            op.type === "add_page" ||
            op.type === "update_page" ||
            op.type === "move_sheet"
          )
            continue;
          if (op.type === "add_comment") {
            let page: TLRecord | undefined;
            if ("shapeId" in op.anchor) {
              page = editable(txn, op.anchor.shapeId);
              for (
                let depth = 0;
                page?.typeName === "shape" && depth < 32;
                depth++
              )
                page = txn.get(page.parentId);
            } else page = txn.get(op.anchor.pageId);
            if (page?.typeName !== "page")
              return fail("Comment anchor must belong to an existing page.");
            const threadId = createCommentThreadId(`agent-${op.id}`),
              commentId = createCommentId(`agent-${op.id}`);
            if (txn.get(threadId) || txn.get(commentId))
              throw new AgentError(409, "Comment ID already exists.");
            const anchor =
              "shapeId" in op.anchor
                ? {
                    type: "shape" as const,
                    ...op.anchor,
                    shapeId: op.anchor.shapeId as TLShapeId,
                    isPrecise: true,
                  }
                : { type: "point" as const, x: op.anchor.x, y: op.anchor.y };
            const thread = createCommentThread({
              pageId: page.id,
              anchor,
              createdBy: actor.user.id,
              ...(op.region
                ? {
                    meta: {
                      reviewRegion: {
                        ...op.region,
                        shapeId:
                          "shapeId" in op.anchor ? op.anchor.shapeId : "",
                        pinX: 1,
                        pinY: 1,
                      },
                    },
                  }
                : {}),
            });
            if (
              op.region &&
              (!("shapeId" in op.anchor) ||
                op.region.x + op.region.w > 1 ||
                op.region.y + op.region.h > 1)
            )
              return fail(
                "Area comments need a shape anchor and a region within its bounds.",
              );
            if (op.resolved)
              thread.resolved = { at: Date.now(), by: actor.user.id };
            const comment = createComment({
              pageId: page.id,
              threadId,
              authorId: actor.user.id,
              body: {
                type: "doc",
                content: [
                  ...(richText(op.text).content ?? []),
                  ...(op.mentions.length
                    ? [
                        {
                          type: "paragraph",
                          content: op.mentions.flatMap((mention, index) => [
                            ...(index ? [{ type: "text", text: " " }] : []),
                            {
                              type: "mention",
                              attrs: {
                                ...mention,
                                occurrenceId: crypto.randomUUID(),
                              },
                            },
                          ]),
                        },
                      ]
                    : []),
                ],
              },
              meta: {
                author: actor.user.name,
                ...(op.action || op.mentions.length
                  ? {
                      review: JSON.parse(
                        JSON.stringify(
                          reviewDraft({
                            mentions: op.mentions.map((m) => m.userId),
                            ...(op.action ? { action: op.action } : {}),
                          }),
                        ),
                      ),
                    }
                  : {}),
              },
            });
            set({ ...thread, id: threadId });
            set({ ...comment, id: commentId });
          } else if (op.type === "add_sheet") {
            const pages = [...txn.values()].filter(
              (r) => r.typeName === "page",
            );
            const page = op.pageId
              ? pages.find((p) => p.id === op.pageId)
              : pages.length === 1
                ? pages[0]
                : undefined;
            if (!page)
              return fail(
                "Choose an existing page ID when the board has multiple pages.",
              );
            if (txn.get(op.id))
              throw new AgentError(409, "Shape ID already exists.");
            const siblings = [...txn.values()]
              .filter(
                (r): r is TLShape =>
                  r.typeName === "shape" && r.parentId === page.id,
              )
              .sort((a, b) =>
                a.index < b.index ? -1 : a.index > b.index ? 1 : 0,
              );
            const order =
              Math.max(
                0,
                ...[...txn.values()]
                  .filter((r) => r.typeName === "shape" && r.type === "sheet")
                  .map((r) => Number(r.meta.order ?? 0)),
              ) + 1;
            set({
              id: op.id as TLShapeId,
              typeName: "shape",
              type: "sheet",
              parentId: page.id,
              index: getIndexAbove(siblings.at(-1)?.index),
              x: op.x,
              y: op.y,
              rotation: 0,
              isLocked: false,
              opacity: 1,
              meta: {
                order,
                agent: { userId: actor.user.id, batchId: command.batchId },
              },
              props: {
                name: op.name,
                w: op.w,
                h: op.h,
                paper: op.paper,
                revision: op.revisionLabel,
                source: "",
                preview: "",
                page: 1,
                metersPerUnit: 0,
                calibration: "",
              },
            } as TLRecord);
          } else if (
            op.type === "add_text" ||
            op.type === "add_image" ||
            op.type === "add_geometry" ||
            op.type === "add_line" ||
            op.type === "add_scene"
          ) {
            if (Boolean(op.sheetId) === Boolean(op.pageId))
              return fail("Provide exactly one sheetId or pageId.");
            const parent = op.sheetId
              ? editable(txn, op.sheetId)
              : txn.get(op.pageId!);
            if (
              !parent ||
              (op.sheetId
                ? parent.typeName !== "shape" || parent.type !== "sheet"
                : parent.typeName !== "page")
            )
              return fail("Choose an existing sheet or canvas page.");
            if (txn.get(op.id))
              throw new AgentError(409, "Shape ID already exists.");
            const siblings = [...txn.values()]
              .filter(
                (r): r is TLShape =>
                  r.typeName === "shape" && r.parentId === parent.id,
              )
              .sort((a, b) =>
                a.index < b.index ? -1 : a.index > b.index ? 1 : 0,
              );
            const base = {
              id: op.id as TLShapeId,
              typeName: "shape" as const,
              parentId: parent.id,
              index: getIndexAbove(siblings.at(-1)?.index),
              x: op.x,
              y: op.y,
              rotation: 0,
              isLocked: false,
              opacity: 1,
              meta: {
                agent: { userId: actor.user.id, batchId: command.batchId },
              },
            };
            if (op.type === "add_text")
              set({
                ...base,
                type: "text",
                meta: {
                  ...base.meta,
                  calqueTextSize: op.fontSize,
                  ...(op.color.startsWith("#") ? { exactColor: op.color } : {}),
                },
                props: {
                  color: op.color.startsWith("#") ? "black" : op.color,
                  size: "m",
                  font: "sans",
                  textAlign: op.align,
                  w: op.w,
                  autoSize: false,
                  scale: 1,
                  richText: richText(op.text, op.bold, op.boldRanges),
                },
              } as TLRecord);
            else if (op.type === "add_line") {
              if (op.x === op.x2 && op.y === op.y2)
                return fail("A line needs two distinct points.");
              set({
                ...base,
                type: "mark",
                props: {
                  kind: "line",
                  points: [
                    { x: 0, y: 0 },
                    { x: op.x2 - op.x, y: op.y2 - op.y },
                  ],
                  color: op.color,
                  weight: op.weight,
                },
              } as TLRecord);
            } else if (op.type === "add_geometry") {
              set({
                ...base,
                type: "geo",
                meta: {
                  ...base.meta,
                  ...(op.color.startsWith("#") ? { exactColor: op.color } : {}),
                },
                props: {
                  geo: op.geo,
                  w: op.w,
                  h: op.h,
                  color: op.color.startsWith("#") ? "black" : op.color,
                  fill: op.fill,
                  dash: "solid",
                  size: op.size,
                  font: "sans",
                  align: "middle",
                  verticalAlign: "middle",
                  richText: richText(""),
                  labelColor: op.color.startsWith("#") ? "black" : op.color,
                  url: "",
                  growY: 0,
                  scale: 1,
                  flipX: false,
                  flipY: false,
                },
              } as TLRecord);
            } else if (op.type === "add_scene") {
              set({
                ...base,
                type: "scene",
                props: {
                  w: op.w,
                  h: op.h,
                  name: op.name,
                  spec: checkedSceneSpec(op.spec, fail),
                  view: "iso",
                  camera: [],
                },
              } as TLRecord);
            } else {
              const existingAsset = txn.get(op.assetId);
              if (
                existingAsset &&
                (existingAsset.typeName !== "asset" ||
                  existingAsset.type !== "image" ||
                  existingAsset.props.src !== op.src ||
                  existingAsset.props.w !== op.pixelWidth ||
                  existingAsset.props.h !== op.pixelHeight ||
                  existingAsset.props.mimeType !== op.mimeType)
              )
                throw new AgentError(
                  409,
                  "Asset ID already exists with different image content.",
                );
              if (!existingAsset)
                set({
                  id: op.assetId as TLAssetId,
                  typeName: "asset",
                  type: "image",
                  meta: {
                    imagePreviews: true,
                    ...(op.provenance ? { provenance: op.provenance } : {}),
                  },
                  props: {
                    name: op.name,
                    src: op.src,
                    w: op.pixelWidth,
                    h: op.pixelHeight,
                    mimeType: op.mimeType,
                    isAnimated: false,
                  },
                } as TLRecord);
              let imageBase = base;
              if (op.caption) {
                const c = op.caption,
                  cw = c.width ?? op.w;
                const ch = captionHeight(c, cw);
                const cardId = `${op.id}-card` as TLShapeId;
                const captionId = `${op.id}-caption` as TLShapeId;
                if (txn.get(cardId) || txn.get(captionId))
                  throw new AgentError(409, "Gallery card ID already exists.");
                set({
                  ...base,
                  id: cardId,
                  type: "group",
                  props: {},
                } as TLRecord);
                const imageX = c.position === "left" ? cw + c.gap : 0;
                const imageY = c.position === "above" ? ch + c.gap : 0;
                imageBase = { ...base, parentId: cardId, x: imageX, y: imageY };
                set({
                  ...base,
                  id: captionId,
                  parentId: cardId,
                  type: "text",
                  index: getIndexAbove(base.index),
                  x: c.position === "right" ? op.w + c.gap : 0,
                  y: c.position === "below" ? op.h + c.gap : 0,
                  meta: {
                    ...base.meta,
                    calqueTextSize: c.fontSize,
                    captionFor: op.id,
                  },
                  props: {
                    color: c.color,
                    size: "m",
                    font: "sans",
                    textAlign: c.align,
                    w: cw,
                    autoSize: false,
                    scale: 1,
                    richText: richText(c.text, c.bold),
                  },
                } as TLRecord);
              }
              set({
                ...imageBase,
                type: "image",
                props: {
                  w: op.w,
                  h: op.h,
                  assetId: op.assetId as TLAssetId,
                  playing: true,
                  url: "",
                  crop: null,
                  flipX: false,
                  flipY: false,
                  altText: op.name,
                },
              } as TLRecord);
            }
          } else {
            const shape = editable(txn, op.id);
            let next: TLShape = {
              ...shape,
              meta: {
                ...shape.meta,
                agent: { userId: actor.user.id, batchId: command.batchId },
              },
            };
            if (op.type === "set_color") {
              if (shape.type !== "text" && shape.type !== "geo")
                throw new Error(
                  "Exact colour updates support text and geometry",
                );
              next = {
                ...next,
                props: {
                  ...shape.props,
                  color: op.color.startsWith("#") ? "black" : op.color,
                },
                meta: {
                  ...next.meta,
                  exactColor: op.color.startsWith("#") ? op.color : null,
                  exactStrokeColor: op.strokeColor ?? null,
                  exactFillColor: op.fillColor ?? null,
                },
              } as TLShape;
            }
            if (op.type === "update_scene") {
              if (shape.type !== "scene")
                return fail("update_scene only edits a 3D model.");
              next = {
                ...next,
                props: {
                  ...shape.props,
                  spec: checkedSceneSpec(op.spec, fail),
                  ...(op.name ? { name: op.name } : {}),
                },
              } as TLShape;
            }
            if (op.type === "move") next = { ...next, x: op.x, y: op.y };
            if (op.type === "update_text" || op.type === "edit_text") {
              if (nativeDocument(shape))
                return fail(
                  "Edit native document text in the workspace using a writer session.",
                );
              if (!("richText" in shape.props))
                return fail(
                  "Only text and document objects support update_text. To put words on a shape, add_text on top of it.",
                );
              const size = op.type === "update_text" ? op.fontSize : undefined;
              let content: TLRichText;
              if (op.type === "edit_text")
                content = editTextRange(
                  shape.props.richText,
                  op.start,
                  op.end,
                  op.text,
                );
              else if (op.richText !== undefined) {
                content = richTextValidator.validate(op.richText);
                if (plainText(content) !== op.text)
                  throw new Error(
                    "richText and text must describe the same content",
                  );
              } else {
                content = richText(
                  op.text,
                  op.bold ?? false,
                  op.boldRanges ?? [],
                );
                if (op.formatting !== "replace")
                  content = preserveTextStyle(
                    shape.props.richText,
                    content,
                    op.bold !== undefined || op.boldRanges !== undefined,
                  );
              }
              next = {
                ...next,
                props: {
                  ...shape.props,
                  richText: content,
                  ...(shape.type === "document" && size
                    ? { fontSize: size }
                    : {}),
                },
                meta: {
                  ...next.meta,
                  ...(shape.type === "text" && size
                    ? { calqueTextSize: size }
                    : {}),
                },
              } as TLShape;
            }
            if (op.type === "resize") {
              if (
                typeof shape.meta.metersPerUnit === "number" &&
                shape.meta.metersPerUnit > 0
              )
                return fail(
                  "Resize calibrated content in the app to preserve scale.",
                );
              if (
                ![
                  "text",
                  "image",
                  "document",
                  "table",
                  "geo",
                  "trace",
                ].includes(shape.type)
              )
                return fail(
                  "Resize supports text, image, document and table objects. Sheet calibration is preserved.",
                );
              if (shape.type === "text" && op.h !== undefined)
                return fail(
                  "Text height is determined by wrapping; provide width only.",
                );
              if (shape.type !== "text" && op.h === undefined)
                return fail("Provide both width and height.");
              next = {
                ...next,
                props: {
                  ...shape.props,
                  w: op.w,
                  ...(op.h ? { h: op.h } : {}),
                  ...(shape.type === "text" ? { autoSize: false } : {}),
                },
              } as TLShape;
            }
            set(next);
          }
        }
      }
      validate(txn);
      journal.put({
        batchId: command.batchId,
        actorId: actor.user.id,
        actorName: actor.user.name,
        createdAt: Date.now(),
        revision: txn.getClock(),
        request,
        changes,
      });
      return {
        batchId: command.batchId,
        revision: txn.getClock(),
        changedIds: Object.keys(changes),
        replayed: false,
      };
    },
    { id: `agent:${command.batchId}` },
  ).result;
}
